import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IPatientSearchListItem } from "../../@types/Patient";
import { ReturnArrow } from "../../assets/icons/ReturnArrow";
import { usePatientSearchList } from "../../hooks/queries/patients";
import { useArrowsListennerHandler } from "../../hooks/useArrowsListennerHandler";
import useIsMobile from "../../hooks/useIsMobile";
import { ISearchOption, SearchField } from "../SearchField";
import {
  MobileSearchBarContainer,
  SearchBarContainer,
  SearchIcon,
} from "./SearchBar.styles";

function formatPatientLabel(patient: IPatientSearchListItem): string {
  const fullName = `${patient.firstName ?? ""} ${
    patient.lastName ?? ""
  }`.trim();
  const ageSex = `${patient.age ?? ""}${(patient.sex ?? "").toUpperCase()}`;
  return ageSex ? `${fullName} (${ageSex})` : fullName;
}

function formatPatientSecondary(patient: IPatientSearchListItem): string {
  const parts: string[] = [];
  if (patient.ihi) parts.push(`IHI:${patient.ihi}`);
  if (patient.mrn) parts.push(`MRN:${patient.mrn}`);
  return parts.join(" - ");
}

function buildOption(patient: IPatientSearchListItem): ISearchOption {
  const label = formatPatientLabel(patient);
  const secondary = formatPatientSecondary(patient);
  const searchText = [label, patient.ihi ?? "", patient.mrn ?? "", secondary]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    label,
    value: patient.id.toString(),
    key: patient.id.toString(),
    secondary,
    searchText,
  };
}

export function SearchBar() {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { patients } = usePatientSearchList();
  const { disablePatientListArrowNav, enablePatientListArrowNav } =
    useArrowsListennerHandler();

  const optionsList = useMemo<ISearchOption[]>(
    () => patients.map(buildOption),
    [patients]
  );

  const handleRedirect = (id: string) => {
    setSearchValue(null);
    navigate(`/patients/${id}/overview`);
  };

  const handleMobileIconClick = () => {
    setIsMobileSearchActive(true);
  };

  const handleSearchBarClose = () => {
    setIsMobileSearchActive(false);
  };

  if (isMobile) {
    if (isMobileSearchActive) {
      return (
        <MobileSearchBarContainer>
          <ReturnArrow onClick={handleSearchBarClose} />
          <SearchBarContainer style={{ width: "80%" }}>
            <SearchField
              options={optionsList}
              placeholder={t("common_searchPatients")}
              onValueChange={handleRedirect}
              value={searchValue}
            />
          </SearchBarContainer>
        </MobileSearchBarContainer>
      );
    }
    return (
      <SearchBarContainer>
        <SearchIcon onClick={handleMobileIconClick}>
          <MagnifyingGlassIcon />
        </SearchIcon>
      </SearchBarContainer>
    );
  }

  return (
    <SearchBarContainer>
      <SearchField
        onFocus={disablePatientListArrowNav}
        onBlur={enablePatientListArrowNav}
        options={optionsList}
        placeholder={t("common_searchPatients")}
        onValueChange={handleRedirect}
        value={searchValue}
      />
    </SearchBarContainer>
  );
}
